import React from 'react';
import {
  GuidePage, Section, Steps, QuestionList, WhatHappensNext, Trouble, Footnote, Para, Bullets, Callout,
  startCaseStep, subjectRow, TROUBLE_CATEGORY_MISSING, TEAM_EMAIL, brand,
} from '../GuideKit';

/* Operations → Salesforce → New List View / Modify ListView.
 *
 * Written against the live flow Prosperity_Workflow_Create_New_Case_Ops
 * (version active 2026-10-04): screens "New List View Screen" for New List
 * View, and "Existing List Views" → "Modify List View Details" (or "No
 * Existing List View") for Modify ListView. Both sub-categories manage the
 * same thing - your own custom list views, tracked as Activity_Details__c
 * rows with Division "List View" - so they share one page. New License and
 * Report Problem are separate tasks with their own pages.
 *
 * The Modify path is documented as intended (users who have list views see
 * the table; users with none see the "No Existing List View" message). See
 * the build report for the Existing_List_Views_Decision defect.
 */

const OFFICE_LOOKUP = [
  'Office',
  <>Required. The screen asks: <em>{'"Please confirm the Office/Team you are with. If you do not see your Office/Team, please reach out to the Operations Team."'}</em> Your office&rsquo;s abbreviation becomes the start of the list view&rsquo;s name.</>,
];

const STEPS = [
  startCaseStep({
    recordType: 'Operations',
    category: 'Salesforce',
    subCategory: 'New List View or Modify ListView',
    subject: subjectRow('New list view - sleeves by model', { noAccount: true }),
    lookups: [OFFICE_LOOKUP],
    note: (
      <>
        There is no Household picker on Salesforce requests. <strong>New List View</strong> takes you straight to
        the list view details; <strong>Modify ListView</strong> first asks you to pick which of your list views
        to change.
      </>
    ),
  }),
  {
    title: 'New List View: describe the view',
    body: (
      <>
        <p style={{ marginBottom: '0.5rem' }}>
          The <strong>New List View Details</strong> screen shows how many custom list views you currently have
          (<em>Users may request up to 5 Custom List Views</em>). Choose where the view should live, whether to
          share it, and how many columns it needs, then fill in the <em>Additional Details</em> box.
        </p>
        <p style={{ margin: 0 }}>
          Nothing else appears until Additional Details has something in it. The name field, one box per
          column (5 to 10, matching the number you picked) and the acknowledgement then appear below it.
        </p>
      </>
    ),
  },
  {
    title: 'Modify ListView: pick the view, then say what changes',
    body: (
      <>
        <p style={{ marginBottom: '0.5rem' }}>
          The <strong>Existing List Views</strong> screen shows a <strong>Current User List Views</strong> table
          with List View Name, List View Object and Description. Pick one row - it is required. If you have no
          custom list views you see a <strong>No Existing List View</strong> message instead; go back and choose
          New List View.
        </p>
        <p style={{ margin: 0 }}>
          The <strong>Modify List View Details</strong> screen then lists the selected view&rsquo;s current
          Column 1 to Column 10. Say whether you are modifying or removing it, how it should be shared, and
          which columns change, then fill in Additional Details. A box appears for each column you ticked,
          pre-filled with its current value - overwrite it with the new one.
        </p>
      </>
    ),
  },
  {
    title: 'Acknowledge and submit',
    body: (
      <p style={{ margin: 0 }}>
        Read the Operations and Trading notice at the foot of the screen, select <em>I have read, understand,
        and agree to the above information</em> under Acknowledgement and click Next. FSC creates the case and
        opens it. Note the case number for follow-up.
      </p>
    ),
  },
];

const ADDITIONAL_DETAILS = 'Additional Details (Please note: If there is an existing list view that meets this requirement, the current one will not be made)';

const QUESTIONS = [
  {
    screen: 'New List View Details (New List View)',
    q: 'Where would you like this List View to be?',
    when: 'Always - this is the first question',
    options: ['Case', 'Household', 'Registration', 'Sleeve'],
  },
  {
    q: 'Do you want to share this List View with other Users you work with or the entire Org?',
    when: 'Always',
    options: [
      'Yes (Share this view with everyone in my Org)',
      'Yes (I want to share this list view with specific Users)',
      'No (I do not want to share this list view with anyone)',
    ],
  },
  {
    q: 'Please provide a list of the Users you would like to Share with',
    when: 'Only if you chose Yes (I want to share this list view with specific Users)',
  },
  {
    q: 'A List View can have up to 10 columns and must have at least 5, how many columns do you need?',
    when: 'Always',
    options: ['5', '6', '7', '8', '9', '10'],
    note: 'Your answer decides how many Column boxes appear below. The screen adds: Note: We always recommend that the last column be "Created Date".',
  },
  {
    q: ADDITIONAL_DETAILS,
    when: 'Always',
    note: 'Describe what the view is for and any filters you need (for example, which records it should include). Before you ask, check whether a view you already have, or one shared with you, does the job - if one does, a new one will not be made.',
  },
  {
    q: 'List View name (15 characters max due to Salesforce List View Length name limit)',
    when: 'Once Additional Details has something in it',
    note: 'More than 15 characters stops the screen with "15 Characters MAX". The saved name is your office abbreviation, your last name and first initial, then this name in brackets.',
  },
  {
    q: 'Column 1 · Column 2 · Column 3 · Column 4 · Column 5',
    when: 'Once Additional Details has something in it',
    note: 'Type the field you want in each column, in order.',
  },
  {
    q: 'Column 6 to Column 10',
    when: 'One more box for each column above 5 that you asked for - Column 6 appears for 6 or more, Column 10 only for 10',
  },
  {
    screen: 'Existing List Views (Modify ListView)',
    q: 'Current User List Views',
    when: 'If you have custom list views - pick exactly one row',
    note: 'The screen reads: If you do not see a list view below its possibly you may not have one yet. If so, please return to the previous screen to select the "New List View" option or, if you believe this is an error, please reach out to the Operations Team.',
  },
  {
    screen: 'Modify List View Details (Modify ListView)',
    q: 'Do you want to remove this List View, or Modify it?',
    when: 'Always',
    options: ['I would like to Modify this List View', 'I want to Remove this List View'],
  },
  {
    q: 'Do you want to Share Access to this List View, or Remove Access (If Applicable)?',
    when: 'Always',
    options: [
      'Yes (Share this view with everyone in my Org)',
      'Yes (I want to share this list view with specific Users)',
      'No (I do not want to share this list view with anyone)',
      'No (I would like to stop sharing this list view)',
    ],
  },
  {
    q: 'Please provide a list of the Users you would like to Share with',
    when: 'Only if you chose Yes (I want to share this list view with specific Users)',
  },
  {
    q: 'Which Columns would you like to Modify? (You may select multiple options)',
    when: 'Always - tick one or more',
    options: ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'],
    note: 'Required even if you are removing the view or only changing sharing - say so in Additional Details.',
  },
  {
    q: ADDITIONAL_DETAILS,
    when: 'Always',
    note: 'Explain the change - what to add, drop or re-filter, or why the view should be removed.',
  },
  {
    q: 'Column 1 to Column 10',
    when: 'Once Additional Details has something in it - one box for each column number you ticked',
    note: 'Each box starts with the column\'s current value. Ticking 10 also shows the "Created Date" recommendation.',
  },
];

const TROUBLE = [
  TROUBLE_CATEGORY_MISSING('Operations', 'Salesforce'),
  {
    h: "My Office/Team isn't in the Office picker",
    p: ['The screen asks you to reach out to the Operations Team if you do not see your Office/Team. Do that before submitting - the office abbreviation is part of the list view name.'],
  },
  {
    h: "The name, column boxes and notice haven't appeared",
    p: ['They stay hidden until the Additional Details box has something in it.'],
  },
  {
    h: 'I got "15 Characters MAX"',
    p: ['The List View name allows 15 characters at most. Shorten it - Salesforce caps list view name length.'],
  },
  {
    h: "My list view isn't in the Current User List Views table",
    p: [
      'The table only lists custom list views requested through this form under your own Salesforce user. A view someone else requested, or one shared with you, will not be there.',
      <>The screen notes that list views and list view counts are not transferrable. If you believe a view is missing in error, email the Operations Team at{' '}
        <a href={`mailto:${TEAM_EMAIL.operations}`} style={{ color: brand.tealBlue }}>{TEAM_EMAIL.operations}</a>.</>,
    ],
  },
  {
    h: 'I got a "No Existing List View" message',
    p: ['It reads: It does not appear you have any custom List Views. Go back and choose New List View, or if you believe this is an error, contact the Operations Team rather than clicking on through.'],
  },
];

export default function SalesforceListViews() {
  return (
    <GuidePage
      title="Salesforce List View Requests"
      icon="table"
      path="Operations → Salesforce → New List View or Modify ListView"
      minutes={5}
      intro="How to ask the Prosperity Operations team for a new custom list view in Salesforce, or to change or remove one you already have - what to gather first, every question you will be asked, and what happens after you submit."
    >
      <Section title="Before you start" icon="clipboard-check">
        <Para>Have these ready. The column boxes are free text, so it helps to have the list worked out first.</Para>
        <Bullets items={[
          <><strong>Where the view should live</strong> - Case, Household, Registration or Sleeve.</>,
          <><strong>The columns, in order</strong> - at least 5 and at most 10. The form recommends making the last one Created Date.</>,
          <><strong>A name of 15 characters or fewer.</strong></>,
          <><strong>Who should see it</strong> - just you, everyone in your org, or a list of specific users.</>,
          <><strong>For a change</strong>: which view, which column numbers change and what they become, or that it should be removed.</>,
        ]} />
        <Callout>
          {"Users may request up to 5 Custom List Views. The screen shows how many you currently have, and notes: (Note - List View's and List View Counts are not transferrable)"}
        </Callout>
      </Section>

      <Section title="Submitting the request" icon="list-ol">
        <Steps steps={STEPS} />
      </Section>

      <Section title="Every question, and when it appears" icon="question-circle">
        <Para>Questions are grouped by screen. New List View uses only the first group; Modify ListView uses the other two.</Para>
        <QuestionList questions={QUESTIONS} />
      </Section>

      <WhatHappensNext
        team="operations"
        confirmations={false}
        rows={[]}
        before={
          <Para>
            FSC creates the case, shares it with you and your office, and opens it. Your answers are written onto
            the case for Operations. A New List View request also adds the view to your list of custom list views
            and counts it towards your total; a Modify ListView request records the change against the view you
            picked.
          </Para>
        }
      />

      <Trouble items={TROUBLE} />

      <Footnote>
        This guide describes the request form only. The view is built by the Operations team after you submit; the
        form does not create it in Salesforce by itself.
      </Footnote>
    </GuidePage>
  );
}
